import { useEffect, useRef, useState } from 'preact/hooks';
import { Link, useLocation } from 'wouter-preact';
import type { Theme } from '../../shared/types';
import { Icons } from './Icon';
import { useTheme, useTimeWindow, WINDOWS } from '../lib/contexts';
import { apiPost } from '../lib/api';
import './../styles/components/topbar.css';

const THEME_CYCLE: Record<Theme, Theme> = { dark: 'light', light: 'system', system: 'dark' };
const THEME_LABEL: Record<Theme, string> = { dark: 'Dark', light: 'Light', system: 'System' };

function fmtRun(ts: number): string {
  const d = new Date(ts * 1000);
  const date = d.toISOString().slice(0, 10).replace(/-/g, ' · ');
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} · ${time}`;
}

/** An action may resolve to a status line to show in the menu instead of the default label. */
type Actions = { onRun?: () => Promise<void>; onTestNotify?: () => Promise<string | void> };
type Props = Actions & {
  /** Unix seconds of the last run. Passing this (even null) also shows the chart-window toggle. */
  lastRunAt?: number | null;
  /** Static status text instead of the last-run stamp (Settings page). */
  status?: string;
};

/** Same shell as mynetworth: status line left, segmented controls + theme cycle + menu right. */
export function TopBar({ lastRunAt, status, onRun, onTestNotify }: Props) {
  const { theme, setTheme } = useTheme();
  const { days, setDays } = useTimeWindow();
  return (
    <div class="topbar">
      <div class="topbar-status mono">
        {status ? (
          <span>{status}</span>
        ) : lastRunAt ? (
          <span title={new Date(lastRunAt * 1000).toLocaleString()}>Last run · {fmtRun(lastRunAt)}</span>
        ) : (
          <span aria-hidden="true">&nbsp;</span>
        )}
      </div>
      <div class="topbar-controls">
        {lastRunAt !== undefined && (
          <div class="seg" role="group" aria-label="Chart window">
            {WINDOWS.map((d) => (
              <button key={d} class={`seg-btn ${days === d ? 'is-active' : ''}`} onClick={() => setDays(d)}>{d}D</button>
            ))}
          </div>
        )}
        <button
          class="seg-btn icon-btn"
          aria-label={`Theme: ${THEME_LABEL[theme]}`}
          title={`Theme: ${THEME_LABEL[theme]}`}
          onClick={() => setTheme(THEME_CYCLE[theme])}
        >
          {theme === 'dark' ? <Icons.Moon size={14} /> : theme === 'light' ? <Icons.Sun size={14} /> : <Icons.Monitor size={14} />}
        </button>
        <Menu onRun={onRun} onTestNotify={onTestNotify} />
      </div>
    </div>
  );
}

function Menu({ onRun, onTestNotify }: Actions) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [path] = useLocation();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const act = (label: string, fn: () => Promise<string | void>) => async () => {
    setBusy(true);
    setMsg(null);
    try {
      const out = await fn();
      setMsg(out || label);
    } catch (e) {
      setMsg(`Failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  async function logout() {
    await apiPost('/api/auth/logout', {});
    window.location.replace('/login');
  }

  return (
    <div class="settings" ref={rootRef}>
      <button class="seg-btn icon-btn" onClick={() => setOpen((x) => !x)} aria-label="Menu" aria-expanded={open}>
        <Icons.Settings size={14} />
      </button>
      {open && (
        <div class="settings-menu" role="menu">
          {onRun && (
            <button class="settings-item" role="menuitem" disabled={busy} onClick={act('Run finished', onRun)}>
              <Icons.Play size={14} /> {busy ? 'Working…' : 'Run now'}
            </button>
          )}
          {onTestNotify && (
            <button class="settings-item" role="menuitem" disabled={busy} onClick={act('Test notice sent', onTestNotify)}>
              <Icons.Send size={14} /> Test notifications
            </button>
          )}
          {path === '/settings' ? (
            <Link href="/" class="settings-item" role="menuitem"><Icons.LayoutDashboard size={14} /> Dashboard</Link>
          ) : (
            <Link href="/settings" class="settings-item" role="menuitem"><Icons.KeyRound size={14} /> Vault &amp; passkeys</Link>
          )}
          <button class="settings-item" role="menuitem" onClick={logout}>
            <Icons.LogOut size={14} /> Sign out
          </button>
          {msg && <div class="settings-msg mono">{msg}</div>}
        </div>
      )}
    </div>
  );
}
